import type { GridColDef } from '@mui/x-data-grid';
import type { BillingCycleInvoice } from '@vy/api-definition';
import type { MonthlyClosureReport } from '@vy/types';
import type { ItemOf } from '@vy/utils';
import type { FieldPath } from 'react-hook-form';
import { Price } from '../../../../../components/design/Price';
import { VyTableCell } from '../../../../../components/new-design/table/vy-table/VyTableCell';
import { compareDatesDescending, formatDate } from '../../../../../utils/date';

export const MONTHLY_CLOSURE_REPORT_KEYS = Object.freeze({
  EXTERNAL_ID: 'customerExternalId',
  CUSTOMER_NAME: 'customerName',
  INVOICE_NUMBER: 'invoiceNumber',
  INVOICE_DATE: 'billingCycle.startTime',
  AMOUNT: 'revenueBreakdown.total',
  ERP_ID: 'erpDisplayId',

  CONTRACT_NAME: 'contractName',
} satisfies Record<string, FieldPath<BillingCycleInvoice> | 'amountChangePercentage'>);

export const MONTHLY_CLOSURE_REPORT_COLUMNS: GridColDef<ItemOf<MonthlyClosureReport>>[] = [
  {
    field: MONTHLY_CLOSURE_REPORT_KEYS.EXTERNAL_ID,
    headerName: 'Customer ID',
    headerClassName: 'text-sm',
    headerAlign: 'left',
    align: 'left',
    flex: 1,
    minWidth: 80,
    renderCell: ({ row }) => <VyTableCell value={row.customerExternalId} />,
  },
  {
    field: MONTHLY_CLOSURE_REPORT_KEYS.CUSTOMER_NAME,
    headerName: 'Customer',
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 75,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ row }) => <VyTableCell value={row.customerName} />,
  },
  {
    field: MONTHLY_CLOSURE_REPORT_KEYS.INVOICE_NUMBER,
    headerName: 'Invoice',
    headerAlign: 'left',
    align: 'left',
    minWidth: 90,
    flex: 1,
    renderCell: ({ row }) => <VyTableCell value={row.invoiceNumber} />,
  },
  {
    field: MONTHLY_CLOSURE_REPORT_KEYS.INVOICE_DATE,
    headerName: 'Date',
    headerAlign: 'left',
    align: 'left',
    minWidth: 100,
    flex: 1,
    valueGetter: (_value, row) => formatDate(row.billingCycle.startTime),
    sortComparator: (invoiceStartTime1, invoiceStartTime2) =>
      compareDatesDescending(invoiceStartTime1, invoiceStartTime2),
  },
  {
    field: MONTHLY_CLOSURE_REPORT_KEYS.AMOUNT,
    headerName: 'Amount',
    headerAlign: 'left',
    align: 'left',
    minWidth: 150,
    flex: 1,
    renderCell: ({ row }) => <Price currency={row.currency}>{row.revenueBreakdown.total}</Price>,
  },
  {
    field: MONTHLY_CLOSURE_REPORT_KEYS.ERP_ID,
    headerName: 'ERP ID',
    headerAlign: 'left',
    align: 'left',
    minWidth: 90,
    flex: 1,
    renderCell: ({ row }) => <VyTableCell value={row.erpDisplayId} />,
  },
  {
    field: MONTHLY_CLOSURE_REPORT_KEYS.CONTRACT_NAME,
    headerName: 'Contract',
    headerAlign: 'left',
    align: 'left',
    minWidth: 150,
    flex: 1,
    renderCell: ({ row }) => <VyTableCell value={row.contractName} containerClassName="justify-start" />,
  },
];
